import type { ComponentProps } from 'react'
import { cn } from '@/lib/utils'

export function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn('flex w-full flex-col gap-7', className)}
      {...props}
    />
  )
}

export function Field({ className, ...props }: ComponentProps<'div'>) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: A fieldset is reserved for groups of related fields.
    <div
      role="group"
      data-slot="field"
      className={cn(
        'group/field flex w-full flex-col gap-2 data-[disabled=true]:opacity-50',
        className,
      )}
      {...props}
    />
  )
}

export function FieldLabel({
  className,
  htmlFor,
  children,
  ...props
}: ComponentProps<'label'> & { htmlFor: string }) {
  return (
    <label
      htmlFor={htmlFor}
      data-slot="field-label"
      className={cn(
        'font-nb-architekt text-xs leading-[1.4] tracking-[0.06em] text-foreground group-data-[invalid=true]/field:text-destructive',
        className,
      )}
      {...props}
    >
      {children}
    </label>
  )
}

/** Helper text under a field; the error replaces it while the field is invalid. */
export function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'text-sm text-muted-foreground group-data-[invalid=true]/field:hidden',
        className,
      )}
      {...props}
    />
  )
}

/** A rule with a centered label, e.g. between social and email sign-in. */
export function FieldSeparator({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-separator"
      className={cn(
        'flex items-center gap-3 font-nb-architekt text-xs tracking-[0.06em] text-muted-foreground',
        className,
      )}
      {...props}
    >
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      {children}
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  )
}

export function FieldError({
  className,
  children,
  errors,
  ...props
}: ComponentProps<'div'> & { errors?: Array<{ message?: string } | undefined> }) {
  const messages = [
    ...new Set(errors?.flatMap((error) => (error?.message ? [error.message] : [])) ?? []),
  ]
  const content =
    children ??
    (messages.length === 1 ? (
      messages[0]
    ) : messages.length > 1 ? (
      <ul className="flex list-disc flex-col gap-1 pl-4">
        {messages.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    ) : null)
  if (!content) return null
  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('text-sm text-destructive', className)}
      {...props}
    >
      {content}
    </div>
  )
}
